import $ from 'jquery';
import { Component } from 'react';
import PropTypes from 'prop-types'
import ActionTypes from "mirador/dist/es/src/state/actions/action-types";
import { OSDReferences } from 'mirador/dist/es/src/plugins/OSDReferences';

function forceOSDViewportToRecomputeHeight(osdViewport) {
    const size = osdViewport.containerSize;
    size.y = osdViewport.viewer.element.offsetHeight;
    osdViewport.resize(size);
}

const statuses = {
    AWAITING_MANIFEST: 1,
    AWAITING_UI_RESIZE: 2,
    DONE: 3
}

class WindowEnhancementsComponent extends Component {
    status = statuses.AWAITING_MANIFEST;
    previousCanvasId = null;
    mediaSyncTimeout = null;

    render() {
        return null;
    }

    componentDidMount() {
        this.checkIfShouldShowThumbnails();
        this.checkIfCanvasChanged();
    }

    componentDidUpdate() {
        this.checkIfShouldShowThumbnails();
        this.checkIfCanvasChanged();
    }

    checkIfCanvasChanged() {
        if (this.props.canvasId !== this.previousCanvasId) {
            this.canvasChanged();
            this.previousCanvasId = this.props.canvasId;
        }
    }

    canvasChanged() {
        let manifest = this.manifest();
        let items = manifest && manifest.items;
        if (!items) return;
        let canvas = items.find(x => x.id === this.props.canvasId)
        if (!canvas) return;
        if (canvas.duration) {
            this.afterChangeToAudioVideoCanvas(canvas);
        } else {
            this.afterChangeToNonAudioVideoCanvas(canvas);
        }
    }

    afterChangeToAudioVideoCanvas(canvas) {
        this.scheduleMediaSync(canvas);
    }

    afterChangeToNonAudioVideoCanvas(canvas) {
        window.clearTimeout(this.mediaSyncTimeout);
        this.showPluginMenuIfHidden();
    }

    componentWillUnmount() {
        window.clearTimeout(this.mediaSyncTimeout);
    }

    scheduleMediaSync(canvas, attemptsRemaining = 20) {
        // Mirador can mount the media element after the canvas change event, so retry briefly
        // until the underlying <video>/<audio> exists and can accept caption tracks.
        window.clearTimeout(this.mediaSyncTimeout);
        this.mediaSyncTimeout = window.setTimeout(() => {
            const synced = this.syncMediaElements(canvas);
            if (!synced && attemptsRemaining > 1) {
                this.scheduleMediaSync(canvas, attemptsRemaining - 1);
            }
        }, 250);
    }

    syncMediaElements(canvas) {
        const captionTracks = this.captionTracksForCanvas(canvas);
        const mediaElements = Array.from(this.miradorElement().querySelectorAll('video, audio'));
        mediaElements.forEach(mediaElement => {
            // Preserve the existing workaround that points the <video> directly at its source.
            if (mediaElement.tagName.toLowerCase() === 'video' && mediaElement.lastChild?.src) {
                mediaElement.src = mediaElement.lastChild.src;
            }
            this.syncTimedTextTracks(mediaElement, captionTracks);
        });
        this.hidePluginMenuIfOnlyItemIsImageTools();
        return mediaElements.length > 0;
    }

    captionTracksForCanvas(canvas) {
        return (canvas.annotations || [])
            .flatMap(annotationPage => annotationPage.items || [])
            .map(annotation => annotation.body)
            .filter(body => body?.format === 'text/vtt' && body.id);
    }

    syncTimedTextTracks(mediaElement, captionTracks) {
        // Replace any tracks we previously injected so repeated canvas/media sync stays idempotent.
        mediaElement.querySelectorAll('track[data-dlg-caption-track="true"]').forEach(track => track.remove());
        captionTracks.forEach((captionTrack, index) => {
            const track = document.createElement('track');
            track.dataset.dlgCaptionTrack = 'true';
            track.kind = 'captions';
            track.src = captionTrack.id;
            const label = captionTrack.label?.en?.[0];
            if (label) track.label = label;
            if (captionTrack.language) track.srclang = captionTrack.language;
            if (index === 0) track.default = true;
            mediaElement.appendChild(track);
        });
        this.showDefaultTimedTextTrack(mediaElement, captionTracks);
    }

    showDefaultTimedTextTrack(mediaElement, captionTracks) {
        if (captionTracks.length === 0) return;
        setTimeout(() => {
            // Leave captions available but off by default until the user enables them.
            Array.from(mediaElement.textTracks || []).forEach((textTrack) => {
                textTrack.mode = 'disabled';
            });
        });
    }

    hidePluginMenuIfOnlyItemIsImageTools() {
        const pluginMenuPlugins = (this.miradorElement()?.mirador?.plugins || [])
            .filter(x => x.target === 'WindowTopBarPluginMenu');
        if (pluginMenuPlugins.length === 1 && pluginMenuPlugins[0].component?.name === 'MiradorImageToolsMenuItem') {
            $(this.miradorElement())
                .find('nav[aria-label="Window navigation"] button[aria-label="Window options"]')
                .hide();
        }
    }

    showPluginMenuIfHidden() {
        $(this.miradorElement())
            .find('nav[aria-label="Window navigation"] button[aria-label="Window options"]')
            .show();
    }

    manifest() {
        let manifest = this.props.manifests[this.props.window.manifestId];
        return manifest && manifest.json;
    }

    miradorElement() {
        return document.getElementById(this.props.miradorElementId);
    }

    checkIfShouldShowThumbnails() {
        if (this.status === statuses.DONE) return;
        const miradorWindow = this.props.window;
        const viewport = this.props.viewport;
        let manifest = this.manifest();
        if (this.status === statuses.AWAITING_MANIFEST && manifest) {
            let showThumbnails = false;
            // sequences = old format; items = new format
            let sequences = manifest.sequences || manifest.items;
            if (!sequences || sequences.length > 1 || sequences.length === 0) {
                // Show thumbnails if there are multiple items...
                // also if there are no or undefined sequences (an unexpected condition so do the more flexible thing)
                showThumbnails = true;
            } else {
                // if there's exactly one item, see if it has multiple canvases
                let canvases = sequences[0].canvases;
                if (canvases && canvases.length > 1) {
                    showThumbnails = true;
                }
            }
            if (showThumbnails) {
                this.props.updateThumbnailNav(miradorWindow.thumbnailNavigationId, 'far-bottom');
                this.status = statuses.AWAITING_UI_RESIZE;
            } else {
                this.props.updateThumbnailNav(miradorWindow.thumbnailNavigationId, 'none');
                this.status = statuses.DONE;
            }
        } else if (this.status === statuses.AWAITING_UI_RESIZE && viewport) {
            forceOSDViewportToRecomputeHeight(viewport);
            this.status = statuses.DONE;
        }
    }

    showSideBar() {
        if (!this.props.sideBarOpen) {
            this.props.toggleSidePanel(this.props.windowId);
        }
    }

    hideSideBar() {
        if (this.props.sideBarOpen) {
            this.props.toggleSidePanel(this.props.windowId);
        }
    }

}

function mapStateToProps(state, { windowId }) {
    const osd = OSDReferences.get(windowId);
    return {
        manifests: state.manifests,
        viewport: osd && osd.current && osd.current.viewport,
        windowId,
        sideBarOpen: state.windows[windowId].sideBarOpen,
        canvasId: state.windows[windowId].canvasId,
        miradorElementId: state.config.id
    };
}

function mapDispatchToProps(dispatch) {
    return {
        updateThumbnailNav: (thumbnailNavigationId, position) => dispatch({
            id: thumbnailNavigationId,
            payload: { position },
            type: ActionTypes.UPDATE_COMPANION_WINDOW,
        }),
        toggleSidePanel: (windowId) => dispatch({
            type: ActionTypes.TOGGLE_WINDOW_SIDE_BAR,
            windowId
        })
    };
}

export default {
    target: 'Window',
    mode: 'add',
    name: 'DLGWindowEnhancementsPlugin',
    component: WindowEnhancementsComponent,
    mapStateToProps,
    mapDispatchToProps,
};
